import {ColumnSpec} from '@xh/hoist/cmp/grid';
import {FieldSpec} from '@xh/hoist/data';
import {DEFAULT_GRID_SPEC, FieldCatalog, FieldDataType, GridSpec, GridSpecCapabilities} from '../../../../cmp/gridSpec/GridSpecTypes';
import {
    askCol,
    bidCol,
    estAmountCol,
    estCommissionCol,
    estFeesCol,
    executionPriceCol,
    filledQuantityCol,
    limitPriceCol,
    markCol,
    orderIdCol,
    orderStatusCol,
    orderTickerCol,
    orderTimingCol,
    orderTypeCol,
    progressCol,
    quantityCol,
    transactionTypeCol,
    updatedCol
} from './WorkingOrdersColumns';

/**
 * One GridSpec sets the columns of the working and the filled orders grids. Orders keep their arrival order and the
 * ticker filter in the panel header, so the spec holds columns, sort and display options only.
 */
export const ordersCapabilities: GridSpecCapabilities = {
    grouping: 'none',
    filtering: 'none',
    totals: false,
    leafRows: false
};

const COLUMNS: Record<string, ColumnSpec> = Object.fromEntries(
    [
        orderStatusCol,
        orderIdCol,
        orderTickerCol,
        transactionTypeCol,
        orderTypeCol,
        orderTimingCol,
        limitPriceCol,
        quantityCol,
        filledQuantityCol,
        progressCol,
        executionPriceCol,
        bidCol,
        askCol,
        markCol,
        estAmountCol,
        estCommissionCol,
        estFeesCol,
        {field: 'created', headerName: 'Created', width: 160, cellClass: 'order-muted', renderer: updatedCol.renderer},
        updatedCol
    ].map(col => [col.field as string, col])
);

export const ordersBaseColumn = (field: string): ColumnSpec => COLUMNS[field] ?? {field};

const field = (name: string, type: FieldDataType, label: string, unit?: string, description?: string) => ({
    name,
    type,
    label,
    unit,
    description
});

export const ordersCatalog: FieldCatalog = [
    field('orderStatus', 'string', 'Status'),
    field('id', 'number', 'ID'),
    field('ticker', 'string', 'Ticker'),
    field('transactionType', 'string', 'Action', null, 'Buy, Sell, Short or Cover'),
    field('orderType', 'string', 'Type', null, 'Market, Limit, Stop or Stop Limit'),
    field('orderTiming', 'string', 'TIF', null, 'Time in force'),
    field('limitPrice', 'number', 'Limit', 'USD'),
    field('quantity', 'number', 'Qty', 'shares'),
    field('filledQuantity', 'number', 'Filled', 'shares'),
    field('progress', 'number', 'Progress', '%', 'Filled share of the order quantity'),
    field('executionPrice', 'number', 'Avg Px', 'USD', 'Average fill price'),
    field('bid', 'number', 'Bid', 'USD'),
    field('ask', 'number', 'Ask', 'USD'),
    field('mark', 'number', 'Mark', 'USD'),
    field('estAmount', 'number', 'Est. Amount', 'USD'),
    field('estCommission', 'number', 'Est. Comm.', 'USD'),
    field('estFees', 'number', 'Est. Fees', 'USD'),
    field('created', 'date', 'Created'),
    field('updated', 'date', 'Updated')
];

/** Store fields for the grids - every order field, so any of them can be added as a column, and the fills. The store makes `id` itself. */
export const ordersStoreFields: FieldSpec[] = [
    // dates stay as sent - the renderers format the ISO strings
    ...ordersCatalog.filter(f => f.name !== 'id').map(f => ({name: f.name, type: f.type === 'number' ? 'number' : 'auto'}) as FieldSpec),
    {name: 'fills', type: 'auto'}
];

export const ordersDefaultSpec: GridSpec = {
    ...DEFAULT_GRID_SPEC,
    sortBy: [{colId: 'created', sort: 'asc'}],
    columns: [
        {field: 'orderStatus'},
        {field: 'id'},
        {field: 'ticker'},
        {field: 'transactionType'},
        {field: 'orderType'},
        {field: 'orderTiming'},
        {field: 'limitPrice'},
        {field: 'quantity'},
        {field: 'filledQuantity'},
        {field: 'progress'},
        {field: 'executionPrice'},
        {field: 'bid'},
        {field: 'ask'},
        {field: 'mark'},
        {field: 'estAmount'},
        {field: 'estCommission'},
        {field: 'estFees'},
        {field: 'created', hidden: true},
        {field: 'updated'}
    ]
};
